/**
 * LoginScreen — 仿照 IDE 的登录/注册（用户名/密码 → 网关 token），奶油色重做。
 *
 * 纯展示 + 受控输入；真正的网络调用由容器（App）经 client.login / client.register
 * 走服务端代理。两种模式：登录 / 邀请码注册（IDE 是闭测，注册**必须**带邀请码，
 * 成功即自动登录）。「访客模式」(token=dev) 可不登录体验本地链路（AI 不可用）。
 * 全部用 cream 设计系统。
 */

import { useState, type ReactElement } from "react";
import { Sparkles } from "lucide-react";
import { Card, TextInput, Button } from "../ui/index.js";
import { LanguageToggle } from "../ui/LanguageToggle.js";
import { useTr, type Entry } from "../i18n/context.js";
import { COMMON } from "../i18n/common.js";
import "./LoginScreen.css";

const S = {
  eyebrow: { zh: "AI 剧本工坊", en: "AI story studio" },
  sub: {
    zh: "用你的 Lunaverse 账号登录，即可用 AI 生成可玩的互动剧本。",
    en: "Sign in with your Lunaverse account to generate playable interactive stories with AI.",
  },
  subRegister: {
    zh: "用邀请码创建 Lunaverse 账号，创建成功即自动登录。",
    en: "Create a Lunaverse account with an invite code — you're signed in automatically.",
  },
  username: { zh: "用户名", en: "Username" },
  password: { zh: "密码", en: "Password" },
  inviteCode: { zh: "邀请码", en: "Invite code" },
  inviteHint: { zh: "内测邀请码，形如 LUNA-XXXX-XXXX", en: "Beta invite code, e.g. LUNA-XXXX-XXXX" },
  signIn: { zh: "登录", en: "Sign in" },
  createAccount: { zh: "创建账号", en: "Create account" },
  toRegister: { zh: "没有账号？用邀请码注册", en: "No account? Sign up with an invite code" },
  toLogin: { zh: "已有账号？去登录", en: "Already have an account? Sign in" },
  guest: {
    zh: "以访客模式继续（仅本地演示，AI 不可用）",
    en: "Continue as guest (local demo only, AI unavailable)",
  },
  ariaLogin: { zh: "登录", en: "Sign in" },
  ariaRegister: { zh: "注册", en: "Create account" },
} satisfies Record<string, Entry>;

export interface LoginScreenProps {
  readonly onLogin: (username: string, password: string) => void;
  readonly onRegister: (username: string, password: string, inviteCode: string) => void;
  readonly onGuest: () => void;
  /** Clear any auth error — called when switching sign-in ⇄ sign-up so a stale
   * error from one mode isn't misattributed to the other. */
  readonly onClearError?: () => void;
  readonly error?: string;
  readonly busy?: boolean;
}

export function LoginScreen(props: LoginScreenProps): ReactElement {
  const tr = useTr();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const isSignup = mode === "signup";

  const canSubmit =
    username.trim().length > 0 &&
    password.length > 0 &&
    (!isSignup || inviteCode.trim().length > 0) &&
    !props.busy;

  const submit = (): void => {
    if (!canSubmit) return;
    if (isSignup) props.onRegister(username.trim(), password, inviteCode.trim());
    else props.onLogin(username.trim(), password);
  };

  return (
    <main className="lw-login" aria-label={tr(isSignup ? S.ariaRegister : S.ariaLogin)}>
      <div className="lw-login__aura" aria-hidden="true" />
      <LanguageToggle className="lw-login__lang" />
      <Card className="lw-login__card">
        <form
          className="lw-login__form"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <span className="lw-login__eyebrow">
            <Sparkles size={14} aria-hidden="true" />
            {tr(S.eyebrow)}
          </span>
          <h1 className="lw-login__wordmark">{tr(COMMON.appName)}</h1>
          <p className="lw-login__sub">{tr(isSignup ? S.subRegister : S.sub)}</p>

          <TextInput
            label={tr(S.username)}
            placeholder={tr(S.username)}
            value={username}
            autoComplete="username"
            onChange={(e) => setUsername(e.target.value)}
          />
          <TextInput
            label={tr(S.password)}
            type="password"
            placeholder={tr(S.password)}
            value={password}
            autoComplete={isSignup ? "new-password" : "current-password"}
            onChange={(e) => setPassword(e.target.value)}
          />
          {isSignup ? (
            <TextInput
              label={tr(S.inviteCode)}
              placeholder={tr(S.inviteHint)}
              value={inviteCode}
              autoComplete="one-time-code"
              onChange={(e) => setInviteCode(e.target.value)}
            />
          ) : null}

          {props.error ? (
            <div className="lw-login__error" role="alert">
              {props.error}
            </div>
          ) : null}

          <Button type="submit" variant="primary" size="lg" loading={props.busy} disabled={!canSubmit}>
            {tr(isSignup ? S.createAccount : S.signIn)}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              props.onClearError?.();
              setMode(isSignup ? "signin" : "signup");
            }}
          >
            {tr(isSignup ? S.toLogin : S.toRegister)}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={props.onGuest}>
            {tr(S.guest)}
          </Button>
        </form>
      </Card>
    </main>
  );
}
